import { useCallback, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import useSWR from 'swr';
import { dataGridCol } from '@shared/components/grid/common';
import { Wrapper } from '@styles/CommonStyles';
import { formatPhoneNumber } from '../../../shared/commonFunctions';
import { PhoenixGrid } from '../../../shared/components/grid/PhoenixGrid';
// eslint-disable-next-line import/namespace
import { AddressModal } from './components/AddressesModal';
import { IAddress } from './interfaces';
import { requestCreateAddress, requestDeleteAddress, requestUpdateAddress } from './requests';

export const Addresses = () => {
  const { data: addresses } = useSWR<IAddress[]>('/document-service/shipping-address', {revalidateOnMount: undefined});
  const [currentAddresses, setCurrentAddresses] = useState<IAddress | null>(null);
  const [addressModalPosition, setAddressModalPosition] = useState(0);

  const columns: GridColDef[] = [
    dataGridCol('name', {
      flex: 1.5,
      minWidth: 100,
    }),
    dataGridCol('nickName', {
      flex: 1.5,
      minWidth: 100,
    }),
    dataGridCol('company', {
      flex: 1.5,
      minWidth: 120,
    }),
    dataGridCol('address', {
      flex: 1.5,
      minWidth: 120,
    }),
    dataGridCol('address2', {
      flex: 1.5,
      minWidth: 120,
    }),
    dataGridCol('city', {
      flex: 1.2,
      minWidth: 100,
    }),

    dataGridCol('state', {
      flex: 1.2,
      minWidth: 100,
    }),
    dataGridCol('zip', {
      flex: 1.2,
      minWidth: 100,
      valueGetter: ({ value }) => value.code,
    }),
    dataGridCol('phoneNumber', {
      headerName: 'Phone',
      flex: 1.2,
      minWidth: 120,
      valueFormatter: ({ value }) => formatPhoneNumber(value),
    }),
  ];

  const gridActions = {
    onEdit: (data: IAddress) => {
      setCurrentAddresses(data);
      setAddressModalPosition(prevState => prevState + 1);
    },
    onDelete: (data: IAddress) => requestDeleteAddress(data),
  };

  const handleCreate = () => {
    setCurrentAddresses({} as IAddress);
  };

  const handleSubmit = async (data: IAddress) => {
    if (data.id) {
      await requestUpdateAddress(data);
    } else {
      await requestCreateAddress(data);
    }
    setCurrentAddresses(null);
  };

  const handleClose = useCallback(() => {
    setCurrentAddresses(null);
    setAddressModalPosition(0);
  }, []);

  return (
    <Wrapper>
      <AddressModal
        modalIsOpen={!!currentAddresses}
        addressModalPosition={addressModalPosition}
        handleSubmit={handleSubmit}
        currentAddresses={currentAddresses}
        handleClose={handleClose}
      />

      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        strict
        showClearAllFilter
        buttonTitle="Add New"
        name="Addresses"
        columns={columns}
        loading={!addresses}
        actions={gridActions}
        rows={addresses || []}
        onButtonClick={handleCreate}
      />
    </Wrapper>
  );
};
